import React from 'react';
import { usePortalStore } from '../store';
import {
  BarChart3, ClipboardList, LayoutDashboard, Settings, Zap,
} from 'lucide-react';

/* Mobile priority nav — 5 items max (UX spec: navigation breakpoint pattern).
   Sidebar handles all items at md+. */
const MOBILE_NAV_ITEMS = [
  { id: 'dashboard',  label: 'Dashboard'  },
  { id: 'operations', label: 'Operations' },
  { id: 'backlog',    label: 'Backlog'    },
  { id: 'logbook',    label: 'Logbook'    },
  { id: 'settings',   label: 'Settings'   },
] as const;

function navIcon(id: string, active: boolean): React.ReactNode {
  const cls = `w-5 h-5 transition-colors ${active ? 'text-[--accent-amber]' : 'text-[--text-secondary]'}`;
  switch (id) {
    case 'dashboard':  return <LayoutDashboard className={cls} />;
    case 'operations': return <Zap             className={cls} />;
    case 'backlog':    return <BarChart3        className={cls} />;
    case 'logbook':    return <ClipboardList    className={cls} />;
    case 'settings':   return <Settings         className={cls} />;
    default:           return null;
  }
}

export const BottomNav: React.FC = () => {
  const { currentView, navigateTo, gates, notifications } = usePortalStore();

  const gatesBadge   = gates.filter(g => g.status === 'pending').length;
  const commandBadge = notifications.length;

  const getBadge = (id: string): number => {
    if (id === 'dashboard') return commandBadge + gatesBadge;
    return 0;
  };

  return (
    /* Fixed to viewport bottom — hidden at md+ (sidebar takes over). */
    <nav
      className="fixed bottom-0 left-0 right-0 z-30 md:hidden"
      aria-label="Primary navigation"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderTop: '1px solid var(--border-default)',
        /* Safe-area inset for iPhone notch / home indicator */
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div className="flex items-center justify-around h-14">
        {MOBILE_NAV_ITEMS.map(item => {
          const isActive = currentView === item.id;
          const badge    = getBadge(item.id);
          return (
            <button
              key={item.id}
              onClick={() => navigateTo(item.id)}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
              /* 44×44px minimum touch target (WCAG 2.5.5 mobile AA) */
              className="relative flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[--accent-amber]"
              style={{
                backgroundColor: isActive ? 'var(--active-overlay)' : 'transparent',
              }}
            >
              {navIcon(item.id, isActive)}
              {badge > 0 && (
                <span
                  className="badge-count absolute -top-0.5 -right-0.5"
                  aria-label={`${badge} pending`}
                >
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};
